import { useEffect, useState } from 'react';
import { Autocomplete, TextField } from '@mui/material';
import customAxios from '../../utils/customAxios';
import { GlobalVariables } from '../../globalVar';
import { getToast } from '../../utils/toastConfig';

export default function AssetCatalogFields({
  show,
  value,
  onChange,
  disabled = false,
  retainedEquipmentId = null,
}) {
  const [catalog, setCatalog] = useState({
    equipments: [],
    models: [],
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!show) return;
    let active = true;
    setLoading(true);
    const headers = {
      headers: { Authorization: 'bearer ' + localStorage.getItem('token') },
    };
    Promise.all(
      ['listEquipments', 'models/'].map((endpoint) =>
        customAxios.get(`${GlobalVariables.getURL()}${endpoint}`, headers),
      ),
    )
      .then(([equipments, models]) => {
        if (!active) return;
        const rows = (response) =>
          Array.isArray(response.data)
            ? response.data
            : response.data?.data || [];
        setCatalog({
          equipments: rows(equipments),
          models: rows(models),
        });
      })
      .catch(() => {
        if (active)
          getToast({
            status: 'error',
            title: 'No se pudieron cargar los catálogos de equipos.',
          });
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [show]);

  const [locations, setLocations] = useState([]);
  const [loadingLocations, setLoadingLocations] = useState(false);

  useEffect(() => {
    if (!show || !value.equipment_id) {
      setLocations([]);
      return;
    }
    let active = true;
    setLoadingLocations(true);
    customAxios
      .get(
        `${GlobalVariables.getURL()}getEquipmentLocationsByEquipmentId/${value.equipment_id}`,
        {
          headers: {
            Authorization: 'bearer ' + localStorage.getItem('token'),
          },
        },
      )
      .then((response) => {
        if (!active) return;
        const rows = Array.isArray(response.data) ? response.data : [];
        setLocations(
          rows.map((row) => ({ id: row.location_id, name: row.location_name })),
        );
      })
      .catch(() => {
        if (!active) return;
        setLocations([]);
        getToast({
          status: 'error',
          title: 'No se pudieron cargar las ubicaciones del equipo.',
        });
      })
      .finally(() => {
        if (active) setLoadingLocations(false);
      });
    return () => {
      active = false;
    };
  }, [show, value.equipment_id]);

  const model = catalog.models.find(
    (item) => Number(item.id) === Number(value.model_id),
  );
  const brandId = value.brand_id ?? model?.brand_id;
  const brands = Array.from(
    new Map(
      catalog.models
        .filter((item) => item.brand_id)
        .map((item) => [
          item.brand_id,
          { id: item.brand_id, name: item.brand_name },
        ]),
    ).values(),
  );
  const equipments = catalog.equipments.map((item) => ({
    ...item,
    name: item.equip_name,
  }));
  const models = catalog.models.filter(
    (item) => Number(item.brand_id) === Number(brandId),
  );
  const retainsHiddenEquipment =
    retainedEquipmentId != null &&
    Number(value.equipment_id) === Number(retainedEquipmentId) &&
    !equipments.some((item) => Number(item.id) === Number(retainedEquipmentId));

  const select = (
    field,
    label,
    options,
    selectedId,
    dependentFields = [],
    extraDisabled = false,
    retainMissingValue = false,
  ) => (
    <div className="col-md-6" key={field}>
      <Autocomplete
        fullWidth
        disabled={disabled || loading || extraDisabled}
        options={options}
        value={
          options.find((item) => Number(item.id) === Number(selectedId)) || null
        }
        getOptionLabel={(item) => item.name || ''}
        isOptionEqualToValue={(option, selected) =>
          Number(option.id) === Number(selected.id)
        }
        onChange={(_, selected) =>
          onChange({
            ...value,
            ...Object.fromEntries(
              dependentFields.map((dependent) => [dependent, null]),
            ),
            [field]: selected?.id ?? null,
          })
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            required={!retainMissingValue}
            helperText={
              retainMissingValue
                ? 'El tipo anterior ya no está disponible. Se conservará si no seleccionas otro.'
                : undefined
            }
          />
        )}
      />
    </div>
  );

  return (
    <div className="row" style={{ rowGap: 24 }}>
      {select(
        'equipment_id',
        'Tipo de equipo',
        equipments,
        value.equipment_id,
        ['location_id'],
        false,
        retainsHiddenEquipment,
      )}
      {select(
        'location_id',
        'Ubicación',
        locations,
        value.location_id,
        [],
        !value.equipment_id || loadingLocations,
      )}
      {select('brand_id', 'Marca', brands, brandId, ['model_id'])}
      {select('model_id', 'Modelo', models, value.model_id)}
      {['serial_number', 'num_activo'].map((field) => (
        <div className="col-md-6" key={field}>
          <TextField
            fullWidth
            required
            disabled={disabled}
            label={
              field === 'serial_number' ? 'Número de serie' : 'Número de activo'
            }
            value={value[field] ?? ''}
            onChange={(event) =>
              onChange({ ...value, [field]: event.target.value })
            }
          />
        </div>
      ))}
    </div>
  );
}
